import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Calendar } from "../ui/calendar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus, Trash2, CalendarDays, Clock, Download, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const toKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const parseKey = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const pad = (n) => String(n).padStart(2, "0");
const icsEscape = (s) => (s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

const to12 = (t) => {
  if (!t) return { h: "", m: "", ap: "AM" };
  const [H, M] = t.split(":").map(Number);
  const ap = H >= 12 ? "PM" : "AM";
  let h = H % 12;
  if (h === 0) h = 12;
  return { h: String(h), m: pad(M), ap };
};
const to24 = (h, m, ap) => {
  if (!h) return "";
  let H = Number(h) % 12;
  if (ap === "PM") H += 12;
  return `${pad(H)}:${m || "00"}`;
};

function TimePicker({ value, onChange }) {
  const { h, m, ap } = to12(value);
  const set = (nh, nm, nap) => onChange(to24(nh, nm, nap));
  const sel = "rounded-none border border-[#EBE5E0] bg-white px-2 py-2 text-sm focus:outline-none focus:border-[#A68A6B] disabled:opacity-50";
  const minutes = Array.from({ length: 12 }, (_, i) => pad(i * 5));
  return (
    <div className="mt-1 flex items-center gap-2">
      <select data-testid="event-hour-select" value={h} className={sel}
        onChange={(e) => set(e.target.value, m || "00", ap)}>
        <option value="">--</option>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span className="text-[#5C5651]">:</span>
      <select data-testid="event-minute-select" value={m} className={sel} disabled={!h}
        onChange={(e) => set(h || "12", e.target.value, ap)}>
        {minutes.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
      </select>
      <select data-testid="event-ampm-select" value={ap} className={sel} disabled={!h}
        onChange={(e) => set(h || "12", m || "00", e.target.value)}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
      {h && (
        <button type="button" data-testid="event-time-clear" onClick={() => onChange("")}
          className="text-[11px] uppercase tracking-wider text-[#9b938b] hover:text-[#9E4747]">Clear</button>
      )}
    </div>
  );
}

const localDT = (dateKey, time) => {
  const [y, m, d] = dateKey.split("-");
  const [hh, mm] = (time || "09:00").split(":");
  return `${y}${m}${d}T${pad(hh)}${pad(mm)}00`;
};
const plusOneHour = (dateKey, time) => {
  const [y, m, d] = dateKey.split("-").map(Number);
  const [hh, mm] = (time || "09:00").split(":").map(Number);
  const dt = new Date(y, m - 1, d, hh + 1, mm);
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
};
const nextDayKey = (dateKey) => {
  const d = parseKey(dateKey);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};

const veventLines = (ev) => {
  const lines = [
    "BEGIN:VEVENT",
    `UID:${ev.id || Math.random().toString(36).slice(2)}@atelier`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`,
    `SUMMARY:${icsEscape(ev.title)}`,
  ];
  if (ev.time) {
    lines.push(`DTSTART:${localDT(ev.date, ev.time)}`);
    lines.push(`DTEND:${plusOneHour(ev.date, ev.time)}`);
  } else {
    const start = ev.date.replace(/-/g, "");
    lines.push(`DTSTART;VALUE=DATE:${start}`);
    lines.push(`DTEND;VALUE=DATE:${nextDayKey(ev.date)}`);
  }
  if (ev.notes) lines.push(`DESCRIPTION:${icsEscape(ev.notes)}`);
  lines.push("END:VEVENT");
  return lines;
};

const buildIcs = (events, calName) => {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//WorkRoom//Interior Design//EN", "CALSCALE:GREGORIAN", `X-WR-CALNAME:${icsEscape(calName || "WorkRoom Project")}`];
  events.forEach((ev) => lines.push(...veventLines(ev)));
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
};

const downloadIcs = (events, filename) => {
  const blob = new Blob([buildIcs(events, filename)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(filename || "calendar").replace(/[^a-z0-9]+/gi, "-")}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const googleUrl = (ev) => {
  const dates = ev.time
    ? `${localDT(ev.date, ev.time)}/${plusOneHour(ev.date, ev.time)}`
    : `${ev.date.replace(/-/g, "")}/${nextDayKey(ev.date)}`;
  const params = new URLSearchParams({ action: "TEMPLATE", text: ev.title || "Event", dates, details: ev.notes || "" });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export default function CalendarTab({ projectId, readOnly }) {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [month, setMonth] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: toKey(new Date()), time: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/events`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const eventDays = useMemo(() => events.map((e) => parseKey(e.date)), [events]);
  const selectedKey = toKey(selected);
  const dayEvents = events.filter((e) => e.date === selectedKey)
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const upcoming = useMemo(() => {
    const todayKey = toKey(new Date());
    return events.filter((e) => e.date >= todayKey).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)).slice(0, 6);
  }, [events]);

  const openAdd = () => {
    setForm({ title: "", date: selectedKey, time: "", notes: "" });
    setOpen(true);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/events`, form);
      toast.success("Task added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add task");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => { await api.delete(`/events/${id}`); load(); };

  const fmtDate = (k) => parseKey(k).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const fmt12 = (t) => {
    const { h, m, ap } = to12(t);
    return h ? `${h}:${m} ${ap}` : "";
  };

  return (
    <div className="max-w-5xl space-y-8" data-testid="calendar-tab">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Schedule</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Calendar</h3>
        </div>
        <div className="flex items-center gap-2">
          {events.length > 0 && (
            <Button data-testid="export-ics-btn" onClick={() => downloadIcs(events, "WorkRoom Project")}
              className="rounded-none bg-transparent border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-5 px-5">
              <Download size={15} className="mr-2" /> Export all (.ics)
            </Button>
          )}
          {!readOnly && (
            <Button data-testid="new-event-btn" onClick={openAdd}
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
              <Plus size={15} className="mr-2" /> Add Task
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
        <div className="bg-white p-4 flex justify-center">
          <Calendar mode="single" selected={selected} onSelect={(d) => d && setSelected(d)}
            month={month} onMonthChange={setMonth}
            modifiers={{ hasEvent: eventDays }}
            modifiersClassNames={{ hasEvent: "relative font-semibold text-[#8C7154] after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:bg-[#A68A6B] after:rounded-full" }}
          />
        </div>
        <div className="bg-white p-6" data-testid="day-events">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-xl text-[#2B2622]">{fmtDate(selectedKey)}</h4>
            <span className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{dayEvents.length} task{dayEvents.length === 1 ? "" : "s"}</span>
          </div>
          {dayEvents.length === 0 ? (
            <p className="text-sm text-[#5C5651]">No tasks on this day.</p>
          ) : (
            <div className="space-y-3">
              {dayEvents.map((ev) => (
                <div key={ev.id} className="border border-[#EBE5E0] p-3" data-testid="event-item">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm text-[#2B2622] font-medium">{ev.title}</p>
                      {ev.time && <p className="text-[11px] text-[#9b938b] inline-flex items-center gap-1 mt-0.5"><Clock size={11} /> {fmt12(ev.time)}</p>}
                      {ev.notes && <p className="text-sm text-[#5C5651] mt-1 leading-relaxed">{ev.notes}</p>}
                    </div>
                    {!readOnly && (
                      <button data-testid={`delete-event-${ev.id}`} onClick={() => remove(ev.id)}
                        className="text-[#5C5651] hover:text-[#9E4747] shrink-0"><Trash2 size={14} strokeWidth={1.5} /></button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#F2EFEB]">
                    <a data-testid={`gcal-event-${ev.id}`} href={googleUrl(ev)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#A68A6B] hover:text-[#8C7154]">
                      <CalendarPlus size={13} strokeWidth={1.5} /> Add to Google
                    </a>
                    <button data-testid={`ics-event-${ev.id}`} onClick={() => downloadIcs([ev], ev.title)}
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#A68A6B] hover:text-[#8C7154]">
                      <Download size={13} strokeWidth={1.5} /> .ics
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="font-serif text-2xl text-[#2B2622] mb-4 flex items-center gap-2"><CalendarDays size={20} strokeWidth={1.5} className="text-[#A68A6B]" /> Upcoming</h4>
        {upcoming.length === 0 ? (
          <div className="border border-dashed border-[#D9CBBF] bg-white p-8 text-center text-[#5C5651]">No upcoming tasks.</div>
        ) : (
          <div className="border border-[#EBE5E0] bg-white divide-y divide-[#F2EFEB]">
            {upcoming.map((ev) => (
              <button key={ev.id} onClick={() => { const d = parseKey(ev.date); setSelected(d); setMonth(d); }}
                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-[#F9F8F6]" data-testid="upcoming-item">
                <div className="text-center w-14 shrink-0">
                  <p className="font-serif text-2xl text-[#A68A6B] leading-none">{parseKey(ev.date).getDate()}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">{parseKey(ev.date).toLocaleDateString([], { month: "short" })}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-[#2B2622] font-medium truncate">{ev.title}</p>
                  <p className="text-[11px] text-[#9b938b]">{ev.time ? `${fmt12(ev.time)} · ` : ""}{ev.notes || "Task"}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">Add task</DialogTitle><DialogDescription className="sr-only">Create a new task</DialogDescription></DialogHeader>
          <form onSubmit={add} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Title</Label>
              <Input data-testid="event-title-input" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Date</Label>
                <Input data-testid="event-date-input" type="date" required value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Time</Label>
                <TimePicker value={form.time} onChange={(t) => setForm({ ...form, time: t })} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Notes</Label>
              <Textarea data-testid="event-notes-input" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <Button data-testid="add-event-submit" type="submit" disabled={busy}
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {busy ? "Adding…" : "Add Task"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
